/**
 * Helpers for building {@link SpacePermissions} for integration binding create payloads.
 * Policy lives on the binding; integration-specific options belong in auth_config.
 */

import type { SpacePermissions } from '../../../services/api';

export type MemoryPermissionFlags = Pick<
  SpacePermissions,
  'can_read' | 'can_write' | 'can_search' | 'can_delete'
>;

export function buildFiltersFromCommaForm(input: {
  categoriesComma: string;
  excludeTagsComma: string;
  minImportance: number;
}): SpacePermissions['filters'] | undefined {
  const categories = input.categoriesComma.trim()
    ? input.categoriesComma
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean)
    : undefined;
  const exclude_tags = input.excludeTagsComma.trim()
    ? input.excludeTagsComma
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
    : undefined;
  const importance_min =
    input.minImportance > 1 ? input.minImportance : undefined;

  if (
    (!categories || categories.length === 0) &&
    (!exclude_tags || exclude_tags.length === 0) &&
    importance_min === undefined
  ) {
    return undefined;
  }

  return {
    ...(categories?.length ? { categories } : {}),
    ...(exclude_tags?.length ? { exclude_tags } : {}),
    ...(importance_min !== undefined ? { importance_min } : {}),
  };
}

export interface BuildBindingPermissionsOptions {
  flags: MemoryPermissionFlags;
  categoriesComma: string;
  excludeTagsComma: string;
  minImportance: number;
  /** When set, adds permissions.rate_limits.requests_per_hour */
  requestsPerHour?: number;
  /** When false, omit rate_limits even if requestsPerHour is set (e.g. Claude Desktop). */
  includeRateLimit?: boolean;
}

/**
 * Canonical SpacePermissions for “standard” integration modals (memory + optional filters + optional rate).
 */
export function buildBindingPermissions(
  opts: BuildBindingPermissionsOptions
): SpacePermissions {
  const filters = buildFiltersFromCommaForm({
    categoriesComma: opts.categoriesComma,
    excludeTagsComma: opts.excludeTagsComma,
    minImportance: opts.minImportance,
  });

  const includeRate =
    opts.includeRateLimit !== false &&
    opts.requestsPerHour !== undefined &&
    opts.requestsPerHour !== null;

  const out: SpacePermissions = {
    ...opts.flags,
  };
  if (filters && Object.keys(filters).length > 0) {
    out.filters = filters;
  }
  if (includeRate) {
    out.rate_limits = { requests_per_hour: opts.requestsPerHour! };
  }
  return out;
}
